import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Star, Trash2, Upload, Save, RotateCcw, CheckSquare, Square, Monitor, Smartphone, Film, Image as ImageIcon } from "lucide-react";
import {
  updateProject, uploadImage, deleteImage, moveImage, reorderImages, rotateImage, rotateImagesBulk,
  fetchCategories, mediaUrl, formatApiError, setPoster,
} from "../../lib/api";
import { isVideo, captureFirstFrame } from "../../lib/videoFrame";
import { RichTextEditor } from "../../components/admin/RichTextEditor";
import { PlayBadge } from "../../components/PlayBadge";
import FramePicker from "./FramePicker";

const FALLBACK_CATEGORIES = ["Furniture", "Lighting", "Interiors", "Objects", "Architecture", "Installation"];
const COVER_POSITIONS = [
  "top left", "top", "top right",
  "left", "center", "right",
  "bottom left", "bottom", "bottom right",
];

const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

export default function ProjectEditor({ project, allProjects, onChange, onDirtyChange }) {
  const [name, setName] = useState(project.name);
  const [namePt, setNamePt] = useState(project.name_pt || "");
  const [description, setDescription] = useState(project.description);
  const [descriptionPt, setDescriptionPt] = useState(project.description_pt || "");
  const [selectedCats, setSelectedCats] = useState(project.categories || []);
  const [catObjects, setCatObjects] = useState([]);
  const [coverPos, setCoverPos] = useState(project.cover_position || "center");
  const [images, setImages] = useState(project.images);
  const [coverId, setCoverId] = useState(project.cover_image_id);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [rotatingId, setRotatingId] = useState(null);
  const [bulkRotating, setBulkRotating] = useState(false);
  const [selected, setSelected] = useState([]); // image ids selected for bulk actions
  const [framePickerFor, setFramePickerFor] = useState(null); // video item for thumbnail picker
  const [dragIdx, setDragIdx] = useState(null);
  const dragRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    setName(project.name); setNamePt(project.name_pt || "");
    setDescription(project.description);
    setDescriptionPt(project.description_pt || "");
    setSelectedCats(project.categories || []);
    setCoverPos(project.cover_position || "center");
    setImages(project.images); setCoverId(project.cover_image_id);
    setSelected([]);
  }, [project]);

  useEffect(() => {
    fetchCategories().then(setCatObjects).catch(() => setCatObjects([]));
  }, []);

  // ---- unsaved-changes tracking (text + categories; cover/rotate save instantly) ----
  const dirty =
    name !== project.name ||
    namePt !== (project.name_pt || "") ||
    description !== project.description ||
    descriptionPt !== (project.description_pt || "") ||
    !sameSet(selectedCats, project.categories || []);

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const allCats = Array.from(new Set([
    ...catObjects.map((c) => c.name),
    ...(project.categories || []),
    ...FALLBACK_CATEGORIES,
  ]));

  const toggleCat = (c) =>
    setSelectedCats((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const syncFrom = async () => {
    const list = await onChange();
    const fresh = list.find((p) => p.id === project.id);
    if (fresh) { setImages(fresh.images); setCoverId(fresh.cover_image_id); }
  };

  const persistOrder = async (arr) => {
    try {
      await reorderImages(project.id, arr.map((i) => i.id));
      await onChange();
    } catch (err) { toast.error(formatApiError(err)); syncFrom(); }
  };

  const onDrop = (targetIdx) => {
    const from = dragRef.current;
    dragRef.current = null;
    setDragIdx(null);
    if (from === null || from === targetIdx) return;
    const arr = [...images];
    const [moved] = arr.splice(from, 1);
    arr.splice(targetIdx, 0, moved);
    setImages(arr);
    persistOrder(arr);
  };

  const saveFields = async () => {
    setSaving(true);
    try {
      await updateProject(project.id, {
        name, name_pt: namePt, description, description_pt: descriptionPt,
        categories: selectedCats,
      });
      await onChange();
      toast.success("Saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      try {
        if (isVideo(f)) {
          let frame = null;
          try { frame = await captureFirstFrame(f); } catch (err) { console.warn("video poster capture failed:", err); }
          const res = await uploadImage(project.id, f, undefined,
            frame ? { width: frame.width, height: frame.height } : undefined);
          let posterOk = false;
          if (frame?.blob && res?.id) {
            try { await setPoster(res.id, frame.blob); posterOk = true; } catch (err) { console.warn("poster upload failed:", err); }
          }
          if (!posterOk) {
            toast.warning(`${f.name}: uploaded, but the thumbnail couldn't be generated. Set one via "Thumbnail".`);
          }
          ok++;
        } else {
          await uploadImage(project.id, f); ok++;
        }
      } catch (err) { toast.error(`${f.name}: ${formatApiError(err)}`); }
    }
    await syncFrom();
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    if (ok) toast.success(`${ok} file${ok > 1 ? "s" : ""} uploaded`);
  };

  const setCover = async (imageId) => {
    setCoverId(imageId);
    try { await updateProject(project.id, { cover_image_id: imageId }); await onChange(); toast.success("Cover updated"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const changeCoverPos = async (pos) => {
    setCoverPos(pos);
    try { await updateProject(project.id, { cover_position: pos }); await onChange(); toast.success("Cover position updated"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const rotate = async (imageId) => {
    setRotatingId(imageId);
    try { await rotateImage(imageId); await syncFrom(); toast.success("Image rotated"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setRotatingId(null); }
  };

  const bulkRotate = async () => {
    if (!selected.length) return;
    setBulkRotating(true);
    try {
      const res = await rotateImagesBulk(selected);
      await syncFrom();
      setSelected([]);
      toast.success(`${res.rotated?.length || 0} image${(res.rotated?.length || 0) > 1 ? "s" : ""} rotated`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBulkRotating(false); }
  };

  const toggleSelect = (imageId) =>
    setSelected((prev) => (prev.includes(imageId) ? prev.filter((x) => x !== imageId) : [...prev, imageId]));
  const selectAll = () => setSelected(images.map((i) => i.id));
  const clearSelect = () => setSelected([]);

  const removeImage = async (imageId) => {
    if (!window.confirm("Delete this image?")) return;
    try { await deleteImage(imageId); await syncFrom(); toast.success("Image deleted"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const relocate = async (imageId, targetId) => {
    if (!targetId || targetId === project.id) return;
    try { await moveImage(imageId, targetId); await syncFrom(); toast.success("Image moved"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const coverImg = coverId && images.find((i) => i.id === coverId);
  const coverPreviewSrc = coverImg ? (coverImg.type === "video" ? (coverImg.poster || coverImg.url) : coverImg.url) : null;

  return (
    <div data-testid="project-editor" className="space-y-8">
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">Name (EN)</label>
          <input data-testid="edit-name" value={name} onChange={(e) => setName(e.target.value)}
            className="w-full bg-transparent border border-[var(--vsl-border)] px-3 py-2.5 font-mono text-sm outline-none focus:border-[var(--vsl-fg)]" />
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">Nome (PT)</label>
          <input data-testid="edit-name-pt" value={namePt} onChange={(e) => setNamePt(e.target.value)} placeholder={name}
            className="w-full bg-transparent border border-[var(--vsl-border)] px-3 py-2.5 font-mono text-sm outline-none focus:border-[var(--vsl-fg)]" />
        </div>
        <div className="md:col-span-2">
          <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">Description (EN)</label>
          <RichTextEditor value={description} onChange={setDescription} placeholder="Description" testid="edit-description" />
        </div>
        <div className="md:col-span-2">
          <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">Descrição (PT)</label>
          <RichTextEditor value={descriptionPt} onChange={setDescriptionPt} placeholder="Descrição" testid="edit-description-pt" />
        </div>

        <div className="md:col-span-2">
          <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-3">Categories</label>
          <div data-testid="category-checkboxes" className="flex flex-wrap gap-2">
            {allCats.map((c) => {
              const active = selectedCats.includes(c);
              return (
                <button key={c} type="button" onClick={() => toggleCat(c)}
                  data-testid={`cat-toggle-${c}`}
                  className={`font-mono text-[11px] tracking-wide px-3 py-1.5 border transition-colors ${active ? "border-[var(--vsl-fg)] bg-[var(--vsl-fg)] text-[var(--vsl-bg)]" : "border-[var(--vsl-border)] text-[var(--vsl-muted)] hover:border-[var(--vsl-fg)] hover:text-[var(--vsl-fg)]"}`}>
                  {c}
                </button>
              );
            })}
          </div>
          {selectedCats.length === 0 && (
            <p className="font-mono text-[10px] text-[var(--vsl-muted)] mt-2">No categories selected.</p>
          )}
        </div>

        <div className="md:col-span-2 flex items-center gap-4">
          <button data-testid="save-project-btn" onClick={saveFields} disabled={saving}
            className={`px-5 py-2.5 font-mono text-xs uppercase tracking-wider inline-flex items-center gap-2 transition-colors disabled:opacity-50 ${
              dirty
                ? "bg-[#f17233] text-white hover:bg-[#d95f24]"
                : "bg-[var(--vsl-fg)] text-[var(--vsl-bg)] hover:bg-[var(--vsl-limestone)]"
            }`}>
            <Save size={14} /> {saving ? "Saving…" : "Save details"}
          </button>
          {dirty && (
            <span data-testid="unsaved-indicator" className="font-mono text-[10px] tracking-wider uppercase text-[var(--vsl-limestone)] inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--vsl-limestone)] animate-pulse" /> Unsaved changes
            </span>
          )}
        </div>
      </div>

      <div>
        <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-3">Cover image anchor</label>
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div data-testid="cover-position-picker" className="grid grid-cols-3 gap-1 w-[90px] shrink-0">
            {COVER_POSITIONS.map((pos) => (
              <button key={pos} type="button" onClick={() => changeCoverPos(pos)}
                data-testid={`cover-pos-${pos.replace(" ", "-")}`} title={pos}
                className={`aspect-square border transition-colors ${coverPos === pos ? "bg-[var(--vsl-limestone)] border-[var(--vsl-limestone)]" : "border-[var(--vsl-border)] hover:border-[var(--vsl-fg)]"}`} />
            ))}
          </div>

          {coverImg ? (
            <div data-testid="cover-focus-preview" className="flex flex-wrap items-end gap-6">
              <div>
                <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-[var(--vsl-muted)] mb-2 inline-flex items-center gap-1.5"><Monitor size={11} /> Desktop</p>
                <div className="w-[280px] max-w-full rounded-md border border-[var(--vsl-border)] overflow-hidden bg-[#141312]">
                  <div className="h-4 bg-[#1c1b19] border-b border-[var(--vsl-border)] flex items-center gap-1 px-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--vsl-border)]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--vsl-border)]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--vsl-border)]" />
                  </div>
                  <div className="aspect-video">
                    <img src={mediaUrl(coverPreviewSrc)} alt="desktop preview" style={{ objectPosition: coverPos }} className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
              <div>
                <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-[var(--vsl-muted)] mb-2 inline-flex items-center gap-1.5"><Smartphone size={11} /> Mobile</p>
                <div className="w-[104px] rounded-2xl border-2 border-[var(--vsl-border)] overflow-hidden bg-[#141312] p-1">
                  <div className="relative rounded-xl overflow-hidden aspect-[9/17]">
                    <span className="absolute top-1 left-1/2 -translate-x-1/2 z-10 w-8 h-1 rounded-full bg-black/60" />
                    <img src={mediaUrl(coverPreviewSrc)} alt="mobile preview" style={{ objectPosition: coverPos }} className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
              <p className="font-mono text-[10px] text-[var(--vsl-muted)] max-w-[180px] leading-relaxed">
                Anchor <span className="text-[var(--vsl-fg)] uppercase">{coverPos}</span> — controls how the cover is framed on each screen size.
              </p>
            </div>
          ) : (
            <p className="font-mono text-[10px] text-[var(--vsl-muted)]">Set a cover image to preview framing.</p>
          )}
        </div>
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)]">Media ({images.length}) · <span className="normal-case tracking-normal">drag to reorder</span></label>
          <button data-testid="upload-trigger" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="font-mono text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 border border-[var(--vsl-border)] px-3 py-2 hover:border-[var(--vsl-fg)] transition-colors disabled:opacity-50">
            <Upload size={13} /> {uploading ? "Uploading…" : "Upload media"}
          </button>
          <input ref={fileRef} type="file" accept="image/*,video/mp4,video/webm,video/quicktime,.mov" multiple hidden onChange={onFiles} data-testid="image-file-input" />
        </div>
        <p data-testid="media-hint" className="font-mono text-[10px] text-[var(--vsl-muted)] mb-4">
          Images (JPG, PNG, WebP) · Video formats: MP4, WebM and MOV // max size = 150MB
        </p>

        {images.length > 0 && (
          <div data-testid="bulk-toolbar" className="flex flex-wrap items-center gap-3 mb-4 border border-[var(--vsl-border)] px-3 py-2">
            <button onClick={selected.length === images.length ? clearSelect : selectAll} data-testid="select-all-btn"
              className="font-mono text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 text-[var(--vsl-muted)] hover:text-[var(--vsl-fg)] transition-colors">
              {selected.length === images.length ? <CheckSquare size={13} /> : <Square size={13} />} {selected.length === images.length ? "Clear" : "Select all"}
            </button>
            <span className="font-mono text-[10px] text-[var(--vsl-muted)]">{selected.length} selected</span>
            <button onClick={bulkRotate} disabled={!selected.length || bulkRotating} data-testid="bulk-rotate-btn"
              className="font-mono text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 border border-[var(--vsl-border)] px-3 py-1.5 hover:border-[var(--vsl-fg)] transition-colors disabled:opacity-40">
              <RotateCcw size={12} /> {bulkRotating ? "Rotating…" : "Rotate selected"}
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {images.map((img, idx) => {
            const isSel = selected.includes(img.id);
            return (
              <div key={img.id} data-testid={`admin-image-${img.id}`}
                draggable
                onDragStart={() => { dragRef.current = idx; setDragIdx(idx); }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(idx)}
                onDragEnd={() => { dragRef.current = null; setDragIdx(null); }}
                className={`relative group border cursor-move transition-opacity ${dragIdx === idx ? "opacity-40" : ""} ${isSel ? "border-[var(--vsl-fg)] ring-1 ring-[var(--vsl-fg)]" : coverId === img.id ? "border-[var(--vsl-limestone)]" : "border-[var(--vsl-border)]"}`}>
                <div className="aspect-square bg-[#141312] overflow-hidden relative">
                  {img.type === "video" ? (
                    img.poster ? (
                      <img src={mediaUrl(img.poster)} alt="" draggable={false} loading="lazy"
                        className="w-full h-full object-cover pointer-events-none" />
                    ) : (
                      <video src={mediaUrl(img.url)} muted playsInline preload="metadata" draggable={false}
                        className="w-full h-full object-cover pointer-events-none" />
                    )
                  ) : (
                    <img src={mediaUrl(img.url)} alt="" draggable={false} loading="lazy"
                      className={`w-full h-full object-cover pointer-events-none transition-opacity ${rotatingId === img.id || (bulkRotating && isSel) ? "opacity-30" : ""}`} />
                  )}
                  {img.type === "video" && <PlayBadge size={34} />}
                </div>
                {img.type === "video" && (
                  <span className="absolute bottom-1.5 left-1.5 z-10 bg-black/60 text-white font-mono text-[8px] tracking-wider uppercase px-1.5 py-0.5 inline-flex items-center gap-1"><Film size={9} /> Video</span>
                )}
                <button onClick={(e) => { e.stopPropagation(); toggleSelect(img.id); }} data-testid={`select-image-${img.id}`}
                  className={`absolute top-1.5 right-1.5 z-10 w-5 h-5 flex items-center justify-center border transition-colors ${isSel ? "bg-[var(--vsl-fg)] border-[var(--vsl-fg)] text-[var(--vsl-bg)]" : "bg-black/50 border-white/40 text-transparent hover:border-white"}`}>
                  <CheckSquare size={12} />
                </button>
                {coverId === img.id && (
                  <span className="absolute top-1.5 left-1.5 bg-[var(--vsl-limestone)] text-[var(--vsl-bg)] font-mono text-[8px] tracking-wider uppercase px-1.5 py-0.5">Cover</span>
                )}
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                  {coverId !== img.id && (
                    <button onClick={() => setCover(img.id)} data-testid={`set-cover-${img.id}`}
                      className="font-mono text-[9px] tracking-wider uppercase inline-flex items-center gap-1 hover:text-[var(--vsl-limestone)]"><Star size={11} /> Set cover</button>
                  )}
                  {img.type === "video" ? (
                    <button onClick={() => setFramePickerFor(img)} data-testid={`thumbnail-${img.id}`}
                      className="font-mono text-[9px] tracking-wider uppercase inline-flex items-center gap-1 hover:text-[var(--vsl-limestone)]"><ImageIcon size={11} /> Thumbnail</button>
                  ) : (
                    <button onClick={() => rotate(img.id)} disabled={rotatingId === img.id} data-testid={`rotate-image-${img.id}`}
                      className="font-mono text-[9px] tracking-wider uppercase inline-flex items-center gap-1 hover:text-[var(--vsl-limestone)] disabled:opacity-40">
                      <RotateCcw size={11} /> {rotatingId === img.id ? "Rotating…" : "Rotate"}
                    </button>
                  )}
                  <select defaultValue="" onChange={(e) => relocate(img.id, e.target.value)} data-testid={`move-image-${img.id}`}
                    className="bg-[var(--vsl-bg)] border border-[var(--vsl-border)] font-mono text-[9px] px-1 py-1 max-w-full outline-none">
                    <option value="" disabled>Move to…</option>
                    {allProjects.filter((p) => p.id !== project.id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <button onClick={() => removeImage(img.id)} data-testid={`delete-image-${img.id}`}
                    className="font-mono text-[9px] tracking-wider uppercase inline-flex items-center gap-1 text-red-400 hover:text-red-300"><Trash2 size={11} /> Delete</button>
                </div>
              </div>
            );
          })}
        </div>
        {images.length === 0 && <p className="font-mono text-xs text-[var(--vsl-muted)] mt-4">No media yet. Upload images or videos above.</p>}
      </div>

      {framePickerFor && (
        <FramePicker video={framePickerFor} onClose={() => setFramePickerFor(null)} onSaved={syncFrom} />
      )}
    </div>
  );
}
